import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Eye, EyeOff, Mail, Lock, ShieldCheck, KeyRound, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Fingerprint, RotateCw } from 'lucide-react';
import { fetchPasskeyConfig, loginWithPasskey, passkeysSupported, passkeyErrorMessage } from '@/lib/webauthn';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_TEXT: Record<string, { title: string; subtitle: string }> = {
  totp: { title: 'auth.mfa.totpTitle', subtitle: 'auth.mfa.totpSubtitle' },
  email: { title: 'auth.mfa.emailTitle', subtitle: 'auth.mfa.emailSubtitle' },
  sms: { title: 'auth.mfa.smsTitle', subtitle: 'auth.mfa.smsSubtitle' },
};
const METHOD_DEFAULTS: Record<string, string> = {
  'auth.mfa.totpTitle': 'Tofaktorautentisering', 'auth.mfa.totpSubtitle': 'Skriv inn koden fra autentiseringsappen',
  'auth.mfa.emailTitle': 'Engangskode på e-post', 'auth.mfa.emailSubtitle': 'Vi har sendt en 6-sifret kode til {{hint}}',
  'auth.mfa.smsTitle': 'Engangskode på SMS', 'auth.mfa.smsSubtitle': 'Vi har sendt en 6-sifret kode til mobilnummer {{hint}}',
};

interface TenantBranding {
  id: string;
  name: string;
  slug: string;
  logo_url: string;
  primary_color: string;
  accent_color: string;
}

type Phase = 'login' | 'mfa_setup' | 'mfa_verify';

export default function LoginPage({ onLogin }: any) {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug?: string }>();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null);
  const navigate = useNavigate();

  // MFA state
  const [phase, setPhase] = useState<Phase>('login');
  const [challengeToken, setChallengeToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [savedBackup, setSavedBackup] = useState(false);
  const [copied, setCopied] = useState(false);
  // MFA-metode for gjeldende utfordring (totp | email | sms) + passkey-tilgjengelighet
  const [mfaMethod, setMfaMethod] = useState<string>('totp');
  const [destinationHint, setDestinationHint] = useState<string>('');
  const [fallbackReason, setFallbackReason] = useState<string>('');
  const [resendIn, setResendIn] = useState(0);
  const [passkeyEnabled, setPasskeyEnabled] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    if (!passkeysSupported()) return;
    fetchPasskeyConfig().then((c) => setPasskeyEnabled(!!c.enabled));
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    if (slug) {
      axios.get(`${API}/tenants/lookup/${slug}`)
        .then(({ data }) => { if (data.tenant) setTenantBranding(data.tenant); })
        .catch(() => {});
    }
  }, [slug]);

  const finishLogin = (data: any) => {
    // Stolt enhet («Husk meg») settes som httpOnly-cookie av serveren — ingenting lagres i localStorage.
    onLogin(data.access_token, data.user);
    // `?next=/sti` (kun relativ, same-origin) → tilbake dit brukeren kom fra, f.eks. intern katalog-forhåndsvisning.
    const next = new URLSearchParams(window.location.search).get('next') || '';
    navigate(/^\/(?!\/)/.test(next) ? next : '/');
  };

  const handlePasskeyLogin = async () => {
    setPasskeyBusy(true);
    try {
      const data = await loginWithPasskey(rememberMe);
      finishLogin(data);
    } catch (error: any) {
      toast.error(passkeyErrorMessage(error, t('auth.passkeyFailed', 'Passkey-innlogging mislyktes')));
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleResend = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/mfa/resend`, { challenge_token: challengeToken });
      setDestinationHint(data.destination_hint || destinationHint);
      setResendIn(data.resend_after || 30);
      toast.success(t('auth.newCodeSent', 'Ny kode sendt'));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('auth.couldNotResendCode', 'Kunne ikke sende ny kode'));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { ...formData, remember_me: rememberMe });

      if (data.access_token) {
        finishLogin(data);
        return;
      }
      setChallengeToken(data.challenge_token);
      if (data.mfa_setup_required) {
        // Fetch QR + backup codes to begin setup
        const setup = await axios.post(`${API}/auth/mfa/setup`, { challenge_token: data.challenge_token });
        setQrCode(setup.data.qr_code);
        setSecret(setup.data.secret);
        setBackupCodes(setup.data.backup_codes || []);
        setPhase('mfa_setup');
      } else if (data.mfa_required) {
        setMfaMethod(data.mfa_method || 'totp');
        setDestinationHint(data.destination_hint || '');
        setFallbackReason(data.fallback_reason || '');
        setResendIn(data.mfa_method === 'totp' ? 0 : (data.resend_after || 30));
        setPhase('mfa_verify');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/mfa/verify`, {
        challenge_token: challengeToken, code: mfaCode.trim(),
      });
      finishLogin(data);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('auth.mfaInvalid', 'Ugyldig kode. Prøv igjen.'));
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Hero image with overlay */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1766741406251-820a91756130?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
          alt="Norwegian mountain road"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1419]/95 via-[#1A1F3A]/85 to-[#0F1419]/80" />

        <div className="relative z-10 flex flex-col justify-between w-full p-8 lg:p-12 text-white">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="mb-8">
              <img src="/logo-dark.png" alt="TravelEase.AI" className="w-80 lg:w-96 h-auto object-contain" data-testid="login-hero-logo" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-3 leading-tight text-white">
                {t('login.heroTitle', 'Intelligent transport management')}
              </h2>
              <p className="text-base text-white/70 max-w-md">
                {t('login.heroSubtitle', 'Save time with smart booking, AI-powered trip planning and real-time overview of the entire fleet.')}
              </p>
            </div>

            <div className="space-y-3 pt-4">
              {[
                t('login.feature1', 'Komplett bookingsystem for bedriftskunder'),
                t('login.feature2', 'AI-drevet turoptimalisering'),
                t('login.feature3', 'Sanntids driver- og vehiclestyring'),
              ].map((text: any, i: any) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full flex-shrink-0" />
                  <span className="text-sm text-white/70">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mb-8 text-center">
            <img src="/logo-light.png" alt="TravelEase.AI" className="h-9 w-auto object-contain mx-auto mb-2 dark:hidden" data-testid="login-mobile-logo-light" />
            <img src="/logo-dark.png" alt="TravelEase.AI" className="h-9 w-auto object-contain mx-auto mb-2 hidden dark:block" data-testid="login-mobile-logo-dark" />
            <p className="text-sm text-muted-foreground">{tenantBranding?.name || 'TravelEase.AI'}</p>
          </motion.div>

          <div className="flex justify-end gap-2 mb-6">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            {/* ── Phase: LOGIN ── */}
            {phase === 'login' && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-2">{t('auth.signIn')}</h2>
                  <p className="text-sm text-muted-foreground">{t('auth.enterCredentials')}</p>
                </div>

                {passkeyEnabled && (
                  <div className="mb-6 space-y-3" data-testid="passkey-login-block">
                    <Button type="button" onClick={handlePasskeyLogin} disabled={passkeyBusy || loading}
                      className="w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all border-0"
                      style={{ background: 'linear-gradient(90deg, #0066FF, #06B6D4)' }} data-testid="passkey-login-btn">
                      {passkeyBusy ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                          {t('auth.waitingForPasskey', 'Venter på passkey …')}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2"><Fingerprint className="w-5 h-5" />{t('auth.loginWithPasskey', 'Logg inn med passkey')}</div>
                      )}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">{t('auth.passkeyHint', 'Face ID, Touch ID, Windows Hello eller sikkerhetsnøkkel — uten passord og kode.')}</p>
                    <div className="relative py-1">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                      <div className="relative flex justify-center text-xs"><span className="bg-background px-2 text-muted-foreground">{t('auth.orWithEmailPassword', 'eller med e-post og passord')}</span></div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/50" />
                      <Input
                        id="email" type="email" placeholder={t('auth.emailPH2')}
                        value={formData.email}
                        onChange={(e: any) => setFormData({ ...formData, email: e.target.value })}
                        required className="pl-10 h-12 text-base" data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/50" />
                      <Input
                        id="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                        value={formData.password}
                        onChange={(e: any) => setFormData({ ...formData, password: e.target.value })}
                        required className="pl-10 pr-12 h-12 text-base" data-testid="login-password"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox" className="w-4 h-4 rounded border-border"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        data-testid="login-remember-me"
                      />
                      <span className="text-muted-foreground">{t('auth.rememberMe')}</span>
                    </label>
                    <Link to="/forgot-password" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors">
                      {t('auth.forgotPassword')}
                    </Link>
                  </div>

                  <Button type="submit" disabled={loading}
                    className={`w-full h-12 text-base font-semibold transition-all border-0 ${passkeyEnabled ? '' : 'shadow-lg hover:shadow-xl'}`}
                    variant={passkeyEnabled ? 'secondary' : 'default'}
                    style={passkeyEnabled ? undefined : { background: 'linear-gradient(90deg, #0066FF, #06B6D4)' }} data-testid="login-submit-btn">
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        {t('auth.signingIn')}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">{t('auth.signIn')}<ArrowRight className="w-5 h-5" /></div>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm space-y-2">
                  <div>
                    <span className="text-muted-foreground">{t('auth.noAccount')} </span>
                    <Link to="/register" className="font-semibold text-primary hover:text-primary/80 transition-colors underline-offset-2 hover:underline">
                      {t('auth.signUp')}
                    </Link>
                  </div>
                  <div>
                    <Link to="/request-access" className="font-medium text-muted-foreground hover:text-foreground text-xs transition-colors">
                      {t('auth.requestAccess', 'Be om tilgang som kunde')}
                    </Link>
                  </div>
                </div>
              </>
            )}

            {/* ── Phase: MFA SETUP (first time) ── */}
            {phase === 'mfa_setup' && (
              <div data-testid="mfa-setup-screen">
                <div className="mb-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{t('mfa.setupTitle', 'Aktiver tofaktor­autentisering')}</h2>
                    <p className="text-sm text-muted-foreground">{t('mfa.setupSubtitle', 'Påkrevd for å sikre kontoen din')}</p>
                  </div>
                </div>

                <ol className="text-sm text-muted-foreground space-y-1 mb-4 list-decimal list-inside">
                  <li>{t('mfa.step1', 'Skann QR-koden med en autentiseringsapp (Google Authenticator, Microsoft Authenticator, Authy).')}</li>
                  <li>{t('mfa.step2', 'Skriv inn den 6-sifrede koden for å fullføre.')}</li>
                </ol>

                <div className="flex justify-center mb-4">
                  {qrCode && <img src={qrCode} alt="2FA QR" className="w-44 h-44 rounded-lg border border-border bg-white p-2" data-testid="mfa-qr-code" />}
                </div>

                <div className="text-center mb-4">
                  <p className="text-xs text-muted-foreground">{t('mfa.manualKey', 'Eller skriv inn nøkkelen manuelt:')}</p>
                  <code className="text-xs font-mono break-all" data-testid="mfa-secret">{secret}</code>
                </div>

                {backupCodes.length > 0 && (
                  <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold flex items-center gap-1.5">
                        <KeyRound className="w-4 h-4 text-amber-500" />{t('mfa.backupTitle', 'Sikkerhetskoder')}
                      </span>
                      <button type="button" onClick={copyBackupCodes} className="text-xs flex items-center gap-1 text-blue-600 hover:underline" data-testid="mfa-copy-backup">
                        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied ? t('mfa.copied', 'Kopiert') : t('mfa.copy', 'Kopier')}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{t('mfa.backupHint', 'Lagre disse på et trygt sted. Vises kun én gang. Hver kode kan brukes én gang hvis du mister tilgang til appen.')}</p>
                    <div className="grid grid-cols-2 gap-1 font-mono text-xs" data-testid="mfa-backup-codes">
                      {backupCodes.map((c) => <span key={c} className="bg-background rounded px-2 py-1 text-center">{c}</span>)}
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer">
                      <input type="checkbox" checked={savedBackup} onChange={(e) => setSavedBackup(e.target.checked)} data-testid="mfa-saved-backup" />
                      <span>{t('mfa.savedConfirm', 'Jeg har lagret sikkerhetskodene')}</span>
                    </label>
                  </div>
                )}

                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code" className="text-sm font-medium">{t('mfa.codeLabel', 'Verifiseringskode')}</Label>
                    <Input id="mfa-code" inputMode="numeric" placeholder="000000" value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)} required maxLength={10}
                      className="h-12 text-center text-lg tracking-widest font-mono" data-testid="mfa-code-input" />
                  </div>
                  <Button type="submit" disabled={loading || !savedBackup}
                    className="w-full h-12 text-base font-semibold border-0"
                    style={{ background: 'linear-gradient(90deg, #0066FF, #06B6D4)' }} data-testid="mfa-verify-btn">
                    {loading ? t('auth.signingIn') : t('mfa.activate', 'Aktiver og logg inn')}
                  </Button>
                </form>
              </div>
            )}

            {/* ── Phase: MFA VERIFY (returning) — TOTP / e-post / SMS ── */}
            {phase === 'mfa_verify' && (
              <div data-testid="mfa-verify-screen" data-mfa-method={mfaMethod}>
                <div className="mb-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    {mfaMethod === 'totp' ? <ShieldCheck className="w-6 h-6 text-blue-500" /> : <Mail className="w-6 h-6 text-blue-500" />}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" data-testid="mfa-verify-title">{t((METHOD_TEXT[mfaMethod] || METHOD_TEXT.totp).title, METHOD_DEFAULTS[(METHOD_TEXT[mfaMethod] || METHOD_TEXT.totp).title])}</h2>
                    <p className="text-sm text-muted-foreground" data-testid="mfa-verify-subtitle">{t((METHOD_TEXT[mfaMethod] || METHOD_TEXT.totp).subtitle, { defaultValue: METHOD_DEFAULTS[(METHOD_TEXT[mfaMethod] || METHOD_TEXT.totp).subtitle], hint: destinationHint || t('auth.mfa.yourEmail', 'e-posten din') })}</p>
                  </div>
                </div>
                {fallbackReason && (
                  <p className="mb-4 text-xs rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-amber-700 dark:text-amber-300" data-testid="mfa-fallback-reason">{fallbackReason}</p>
                )}

                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mfa-code-v" className="text-sm font-medium">{t('mfa.codeLabel', 'Verifiseringskode')}</Label>
                    <Input id="mfa-code-v" inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={mfaCode} autoFocus
                      onChange={(e) => setMfaCode(e.target.value)} required maxLength={11}
                      className="h-12 text-center text-lg tracking-widest font-mono" data-testid="mfa-code-input" />
                    <p className="text-xs text-muted-foreground">{t('mfa.useBackup', 'Mistet tilgang? Bruk en av sikkerhetskodene dine.')}</p>
                  </div>
                  {mfaMethod !== 'totp' && (
                    <button type="button" onClick={handleResend} disabled={resendIn > 0}
                      className="w-full flex items-center justify-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 disabled:text-muted-foreground disabled:cursor-not-allowed"
                      data-testid="mfa-resend-btn">
                      <RotateCw className="w-4 h-4" />{resendIn > 0 ? `Send ny kode (${resendIn}s)` : 'Send ny kode'}
                    </button>
                  )}
                  <Button type="submit" disabled={loading}
                    className="w-full h-12 text-base font-semibold border-0"
                    style={{ background: 'linear-gradient(90deg, #0066FF, #06B6D4)' }} data-testid="mfa-verify-btn">
                    {loading ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>
                  <button type="button" onClick={() => { setPhase('login'); setMfaCode(''); }}
                    className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="mfa-back-btn">
                    {t('mfa.back', 'Tilbake')}
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
